import { Button, Card, Container, Flex, Heading } from '@radix-ui/themes';
import { CommonToast } from 'components/common/toast';
import { CommonFormGrid } from 'components/form/grid';
import { FormTextField } from 'components/form/textfield';
import { PATHS } from 'components/main/paths';
import { AuthContext } from 'contexts/auth';
import { FormContext, FormProvider } from 'contexts/form';
import { RX } from 'enums/radix';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface LoginPayload {
  email: string;
  password: string;
}

const EMPTY_MODEL: LoginPayload = {
  email: '',
  password: '',
};

export const LoginHoC = () => {
  return (
    <FormProvider defaultData={EMPTY_MODEL}>
      <Login />
    </FormProvider>
  );
};

const Login = () => {
  const { login } = useContext(AuthContext);
  const { data, errors, setData } = useContext(FormContext);

  const typedData: LoginPayload = data;

  // for redirecting after login
  const goTo = useNavigate();

  const handleLogin = useCallback(async () => {
    const user = await login(typedData.email, typedData.password);

    if (!user) {
      setToastFailure(Date.now());
      return;
    }

    if (user.isAdmin) {
      goTo('/' + PATHS.ADMIN[0].path);
      return;
    }

    if (user.isDealer) {
      goTo('/' + PATHS.DEALER[0].path);
      return;
    }

    if (user.isInvestor) {
      goTo('/' + PATHS.INVESTOR[0].path);
      return;
    }
  }, [typedData.email, typedData.password, login]);

  // try to login/submit the form when user presses enter
  useEffect(() => {
    const callback = (ev: KeyboardEvent) => {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        handleLogin();
      }
    };

    document.addEventListener('keypress', callback);

    return () => {
      document.removeEventListener('keypress', callback);
    };
  }, [handleLogin]);

  const [toastFailure, setToastFailure] = useState<number>();

  return (
    <Container
      size="1"
      style={{
        marginTop: '15vh',
      }}
    >
      <Card size="3">
        <Flex direction="column" gap="4">
          <Heading>Login Form</Heading>

          <CommonFormGrid columns="1">
            <FormTextField
              label="Email"
              type="email"
              onChange={(v) => {
                const payload: Partial<LoginPayload> = {
                  email: v,
                };
                setData(payload);
              }}
              required
            />

            <FormTextField
              label="Password"
              type="password"
              onChange={(v) => {
                const payload: Partial<LoginPayload> = {
                  password: v,
                };
                setData(payload);
              }}
              required
            />
          </CommonFormGrid>

          <Button disabled={errors.length > 0} onClick={handleLogin}>
            Login
          </Button>
        </Flex>
      </Card>

      {toastFailure && (
        <CommonToast
          key={toastFailure}
          color={RX.CLR.DANGER}
          content="Incorrect email and/or password. Please check your inputs and try again."
        />
      )}
    </Container>
  );
};
